import {
  CalendarClock,
  Clock3,
  Copy,
  MessageSquare,
  Radio,
  Timer,
  Trash2,
  UserPlus,
  Users,
  Video,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/Badge";
import { IconButton } from "@/components/ui/IconButton";
import { Skeleton } from "@/components/ui/Skeleton";
import { RoomRecordings } from "@/components/rooms/RoomRecordings";
import type { Room } from "@/lib/api/types";
import { formatDuration, formatNumber, formatRelative } from "@/lib/format";
import { useI18n } from "@/lib/i18n";

function Stat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  hint?: string | undefined;
}) {
  return (
    <div className="min-w-0">
      <p className="flex items-center gap-1.5 text-xs text-ink-muted">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </p>
      <p className="mt-0.5 truncate text-sm font-semibold text-ink" title={hint}>
        {value}
      </p>
    </div>
  );
}

export function RoomCard({
  room,
  now,
  onCopyLink,
  onDelete,
}: {
  room: Room;

  now: number;
  onCopyLink: () => void;
  onDelete: () => void;
}) {
  const { t, locale } = useI18n();
  const stats = room.stats;
  const live = room.activeSince !== null;
  const runningMs = live ? now - new Date(room.activeSince ?? "").getTime() : 0;

  const flags: string[] = [];
  if (room.settings.lobby) flags.push(t("rooms.flag.lobby"));
  if (!room.settings.allowGuests) flags.push(t("rooms.flag.signedInOnly"));
  if (room.settings.hostFirst) flags.push(t("rooms.flag.hostFirst"));
  if (!room.settings.allowRecording) flags.push(t("rooms.flag.noRecording"));
  if (!room.settings.allowChat) flags.push(t("rooms.flag.noChat"));

  return (
    <article className="flex flex-col gap-3 px-5 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-sm font-medium text-ink">{room.title}</h3>
            {live ? (
              <>
                <Badge tone="success">{t("rooms.inCall", { count: room.participantCount })}</Badge>
                <Badge tone="brand">
                  <Timer className="h-3 w-3" />
                  {formatDuration(runningMs / 1000, locale)}
                </Badge>
              </>
            ) : null}
          </div>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
            <span className="font-mono">{room.slug}</span>
            <span>{t("rooms.createdWhen", { when: formatRelative(room.createdAt, locale) })}</span>
            <span>{t("rooms.capacity", { count: room.settings.maxParticipants })}</span>
          </p>
        </div>
        <IconButton label={t("rooms.copyLink")} onClick={onCopyLink}>
          <Copy className="h-4 w-4" />
        </IconButton>
        <IconButton label={t("rooms.deleteThis", { title: room.title })} onClick={onDelete}>
          <Trash2 className="h-4 w-4" />
        </IconButton>
        <Link
          className="inline-flex h-8 items-center gap-2 rounded-xl bg-brand px-3 text-xs font-medium text-brand-ink transition-colors hover:bg-brand-strong"
          to={`/${room.slug}`}
        >
          <Video className="h-4 w-4" /> {t("rooms.open")}
        </Link>
      </div>

      {flags.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {flags.map((flag) => (
            <Badge key={flag}>{flag}</Badge>
          ))}
        </div>
      ) : null}

      {stats.calls === 0 ? (
        <p className="text-xs text-ink-muted">{t("rooms.noHistory")}</p>
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl bg-surface-sunken px-4 py-3 sm:grid-cols-4">
            <Stat
              icon={CalendarClock}
              label={t("rooms.stat.meetings")}
              value={formatNumber(stats.calls, locale)}
            />
            <Stat
              icon={UserPlus}
              label={t("rooms.stat.joins")}
              value={formatNumber(stats.joins, locale)}
              hint={t("rooms.stat.joinsHint", { people: formatNumber(stats.people, locale) })}
            />
            <Stat
              icon={Users}
              label={t("rooms.stat.peak")}
              value={formatNumber(stats.peakParticipants, locale)}
            />
            <Stat
              icon={Clock3}
              label={t("rooms.stat.totalTime")}
              value={formatDuration(stats.totalMs / 1000, locale)}
              hint={t("rooms.stat.longestHint", {
                duration: formatDuration(stats.longestMs / 1000, locale),
              })}
            />
            {stats.messages > 0 ? (
              <Stat
                icon={MessageSquare}
                label={t("rooms.stat.messages")}
                value={formatNumber(stats.messages, locale)}
              />
            ) : null}
            {stats.recordings > 0 ? (
              <Stat
                icon={Radio}
                label={t("rooms.stat.recordings")}
                value={formatNumber(stats.recordings, locale)}
                hint={formatDuration(stats.recordedMs / 1000, locale)}
              />
            ) : null}
            {stats.lastCallAt === null ? null : (
              <Stat
                icon={Timer}
                label={t("rooms.stat.lastMeeting")}
                value={formatRelative(stats.lastCallAt, locale, now)}
              />
            )}
          </dl>

          {}
          {stats.recordings > 0 ? (
            <RoomRecordings count={stats.recordings} slug={room.slug} />
          ) : null}
        </>
      )}
    </article>
  );
}

export function RoomCardSkeleton() {
  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <Skeleton className="h-4 w-44 max-w-full" />
          <Skeleton className="mt-2.5 h-3 w-60 max-w-full" />
        </div>
        <Skeleton className="h-8 w-8 rounded-xl" />
        <Skeleton className="h-8 w-8 rounded-xl" />
        <Skeleton className="h-8 w-20 rounded-xl" />
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl bg-surface-sunken px-4 py-3 sm:grid-cols-4">
        {[0, 1, 2, 3].map((cell) => (
          <div key={cell}>
            <Skeleton className="h-3 w-16" />
            <Skeleton className="mt-1.5 h-4 w-10" />
          </div>
        ))}
      </div>
    </div>
  );
}
